import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame, HeartPulse, DoorClosed, AlertOctagon, Timer, ShieldCheck,
  ShieldAlert, HeartPulse as MedicalIcon, Flame as FireIcon, Wrench,
  ArrowRight,
} from "lucide-react";

const SEV_COLOR = {
  critical: "hsl(var(--destructive))",
  high:     "hsl(var(--warning))",
  medium:   "hsl(var(--primary))",
  low:      "hsl(var(--muted-foreground))",
};

const TEAM_ICON = {
  security:   ShieldAlert,
  police:     ShieldCheck,
  medical:    MedicalIcon,
  fire:       FireIcon,
  facilities: Wrench,
};

const titleCase = (s) =>
  String(s || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const fmtSec = (s) => {
  const v = Number(s ?? 0);
  if (v < 60) return `${Math.round(v)}s`;
  const m = Math.floor(v / 60);
  return `${m}m ${Math.round(v - m * 60)}s`;
};

const KpiPill = ({ icon: Icon, label, value, tone = "default", testid }) => {
  const map = {
    default: "text-foreground",
    danger:  "sev-critical",
    warn:    "sev-medium",
    primary: "text-[hsl(var(--primary))]",
  };
  return (
    <div className="panel-elevated p-2.5 flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 shrink-0 ${map[tone]}`} />
      <div className="min-w-0">
        <div className="micro-label leading-none">{label}</div>
        <div className={`font-display font-black text-lg leading-tight ${map[tone]}`} data-testid={`${testid}-value`}>
          {value}
        </div>
      </div>
    </div>
  );
};

const EmergencyTimeline = ({ items = [] }) => {
  if (items.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground py-4 text-center" data-testid="emergency-timeline-empty">
        No emergency incidents in window.
      </div>
    );
  }
  return (
    <ol className="space-y-2" data-testid="emergency-timeline">
      {items.map((ev) => (
        <li
          key={ev.id}
          className="flex items-start gap-2 p-2 bg-[hsl(var(--surface-elevated))] border-l-2"
          style={{ borderColor: SEV_COLOR[ev.severity] }}
          data-testid={`emergency-timeline-item-${ev.id}`}
        >
          <span
            className="mt-1 w-2 h-2 rounded-full shrink-0"
            style={{ background: SEV_COLOR[ev.severity] }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium text-foreground leading-tight truncate">{ev.title}</div>
            <div className="font-mono text-[10px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-1.5">
              <span>{ev.zone_name}</span>
              <span>·</span>
              <span>{relTime(ev.ts)}</span>
              <span>·</span>
              <span style={{ color: SEV_COLOR[ev.severity] }}>{titleCase(ev.severity)}</span>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
};

const ResponderTeams = ({ teams = [] }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="emergency-teams">
    {teams.map((t) => {
      const Icon = TEAM_ICON[t.team] || ShieldAlert;
      const pct = t.total ? (t.busy / t.total) : 0;
      const busyColor = pct >= 0.66 ? "hsl(var(--destructive))" : pct >= 0.33 ? "hsl(var(--warning))" : "hsl(var(--success))";
      return (
        <div key={t.team} className="panel-elevated p-2" data-testid={`emergency-team-${t.team}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <Icon className="w-3.5 h-3.5" style={{ color: busyColor }} />
            <span className="text-[11px] font-medium truncate">{t.label}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-mono text-sm font-bold" style={{ color: busyColor }}>{t.busy}</span>
            <span className="font-mono text-[10px] text-muted-foreground">/ {t.total} busy</span>
          </div>
          <div className="mt-1 h-1 bg-[hsl(var(--muted))]">
            <div className="h-full" style={{ width: `${pct * 100}%`, background: busyColor }} />
          </div>
          <div className="micro-label text-[9px] mt-1">{t.available} Available</div>
        </div>
      );
    })}
  </div>
);

export const EmergencyMonitoringPanel = ({ data }) => {
  const navigate = useNavigate();
  const d = data || { kpis: {}, timeline: [], teams: [] };
  const k = d.kpis || {};

  return (
    <section className="space-y-3" data-testid="dashboard-section-emergency">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold tracking-tight text-xs">Emergency Monitoring</h2>
        </div>
        <button
          onClick={() => navigate("/incidents")}
          className="btn-tac !px-2 !py-1 text-[10px]"
          data-testid="emergency-open-incidents-btn"
        >
          Open Incidents <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <KpiPill icon={FireIcon}    label="Fire Alerts"      value={k.fire_alerts ?? 0}      tone="danger"  testid="emergency-kpi-fire" />
        <KpiPill icon={HeartPulse}  label="Medical"          value={k.medical_incidents ?? 0} tone="danger"  testid="emergency-kpi-medical" />
        <KpiPill icon={DoorClosed}  label="Blocked Exits"    value={k.blocked_exits ?? 0}    tone="warn"    testid="emergency-kpi-blocked" />
        <KpiPill icon={AlertOctagon} label="Panic Indicators" value={k.panic_indicators ?? 0} tone="danger"  testid="emergency-kpi-panic" />
        <KpiPill icon={Timer}       label="Avg Response"     value={fmtSec(k.avg_response_sec)} tone="primary" testid="emergency-kpi-response" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="panel p-3 lg:col-span-2">
          <div className="h-widget mb-2 flex items-center gap-1">
            <Flame className="w-3 h-3" /> Emergency Timeline
          </div>
          <EmergencyTimeline items={d.timeline} />
        </div>
        <div className="panel p-3">
          <div className="h-widget mb-2 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Response Teams
          </div>
          <ResponderTeams teams={d.teams} />
        </div>
      </div>
    </section>
  );
};

export default EmergencyMonitoringPanel;
